import React, { useState, useEffect, useCallback, useRef } from "react";

// ──────────────────────────────────────────────
// AdminInquiry — #admin 문의/건의 관리 (회원게시판 D_DOCUAI_INQUIRY, docpg)
//   좌: 전체 목록(비공개 포함) · 우: 스레드 상세(질문·첨부·답변) + 답변 작성 + 공개설정.
//   인증 = admin_token. API: /api/admin/inquiry*
//   3레벨 공개: 질문(question_public·작성자) / 답변(answer_public·관리자) / public_title.
// ──────────────────────────────────────────────

const authHeaders = () => {
  const t = localStorage.getItem("admin_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const jsonHeaders = () => ({ ...authHeaders(), "Content-Type": "application/json" });
// 첨부 업로드(/api/inquiry/upload)는 사용자 세션 토큰 사용 — 관리자도 로그인 사용자(admin_token 아님).
const userToken = () => localStorage.getItem("token");

function fmt(d) {
  if (!d) return "";
  const t = new Date(d);
  if (isNaN(t)) return d;
  return `${t.getFullYear()}.${String(t.getMonth() + 1).padStart(2, "0")}.${String(t.getDate()).padStart(2, "0")} ${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`;
}

export default function AdminInquiry({ API_BASE_URL = "" }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState(null); // groupId
  const [thread, setThread] = useState(null);

  const loadList = useCallback(() => {
    fetch(`${API_BASE_URL}/api/admin/inquiry?page=${page}&size=20`, { headers: authHeaders() })
      .then((r) => (r.status === 401 ? Promise.reject("401") : r.json()))
      .then(setData)
      .catch((e) => setData({ items: [], total: 0, error: e === "401" ? "관리자 인증이 만료되었습니다. 다시 로그인해주세요." : "목록을 불러오지 못했습니다." }));
  }, [API_BASE_URL, page]);
  useEffect(loadList, [loadList]);

  const loadThread = useCallback(
    (gid) => {
      setSel(gid);
      setThread(null);
      fetch(`${API_BASE_URL}/api/admin/inquiry/${gid}`, { headers: authHeaders() })
        .then((r) => r.json())
        .then(setThread)
        .catch(() => setThread({ error: "불러오기 실패" }));
    },
    [API_BASE_URL],
  );

  return (
    <div style={{ display: "flex", gap: 16, height: "calc(100vh - 120px)" }}>
      {/* 좌: 목록 */}
      <div style={{ width: 380, minWidth: 320, overflow: "auto", borderRight: "1px solid #eee", paddingRight: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, margin: "4px 0 12px" }}>문의/건의 ({data?.total ?? "…"})</div>
        {data?.error && <div style={{ color: "#c0392b", fontSize: 13, padding: 8 }}>{data.error}</div>}
        {data?.items?.map((it) => (
          <div
            key={it.id}
            onClick={() => loadThread(it.groupId)}
            style={{
              padding: "10px 12px",
              borderBottom: "1px solid #f2f2f4",
              cursor: "pointer",
              background: sel === it.groupId ? "#eef2ff" : "transparent",
              borderRadius: 6,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 13.5, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {it.title}
              </span>
              {it.status === "answered" ? (
                <span style={{ ...tag, background: "#e6f4ea", color: "#137333" }}>답변완료</span>
              ) : (
                <span style={{ ...tag, background: "#fff4e5", color: "#b06000" }}>대기</span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 4, fontSize: 11.5, color: "#6b7280" }}>
              <span>{it.author || "(비공개)"}</span>
              <span>{fmt(it.createdAt).slice(0, 10)}</span>
              <span style={{ marginLeft: "auto" }}>
                {it.questionPublic ? "질문공개" : "질문비공개"} · {it.answerPublic ? "답변공개" : "답변비공개"}
              </span>
            </div>
          </div>
        ))}
        {data?.total > 20 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={btnGhost}>이전</button>
            <span style={{ fontSize: 12, alignSelf: "center" }}>{page}/{Math.ceil(data.total / 20)}</span>
            <button disabled={page >= Math.ceil(data.total / 20)} onClick={() => setPage((p) => p + 1)} style={btnGhost}>다음</button>
          </div>
        )}
      </div>

      {/* 우: 상세 */}
      <div style={{ flex: 1, overflow: "auto", paddingRight: 8 }}>
        {!sel ? (
          <div style={{ color: "#9ca3af", padding: 40, textAlign: "center" }}>왼쪽에서 문의를 선택하세요.</div>
        ) : !thread ? (
          <div style={{ color: "#9ca3af", padding: 40 }}>불러오는 중…</div>
        ) : thread.error ? (
          <div style={{ color: "#c0392b", padding: 40 }}>{thread.error}</div>
        ) : (
          <ThreadDetail API_BASE_URL={API_BASE_URL} thread={thread} onChanged={() => { loadThread(sel); loadList(); }} onDeleted={() => { setSel(null); setThread(null); loadList(); }} />
        )}
      </div>
    </div>
  );
}

function ThreadDetail({ API_BASE_URL, thread, onChanged, onDeleted }) {
  const gid = thread.groupId;
  const [reply, setReply] = useState("");
  const [replyAtt, setReplyAtt] = useState([]);
  const [answerPublic, setAnswerPublic] = useState(thread.answerPublic !== false);
  const [publicTitle, setPublicTitle] = useState(thread.publicTitle || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setAnswerPublic(thread.answerPublic !== false);
    setPublicTitle(thread.publicTitle || "");
    setReply("");
    setReplyAtt([]); // 스레드 전환 시 답변 초안·첨부 초기화
  }, [thread]);

  // 첨부 업로드 — 사용자 게시판과 동일 엔드포인트(/api/inquiry/upload).
  // 인증=사용자 세션 토큰 우선, 없으면 admin_token 폴백(둘 다 공유 JWT_SECRET 서명).
  const uploadReplyFile = async (file) => {
    const tok = userToken() || localStorage.getItem("admin_token");
    if (!tok) return alert("첨부 업로드에는 로그인 세션이 필요합니다.");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API_BASE_URL}/api/inquiry/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tok}` },
        body: fd,
      });
      const d = await r.json();
      if (d.success) setReplyAtt((p) => [...p, d]);
      else alert(d.error || "첨부 업로드 실패");
    } catch {
      alert("첨부 업로드 실패");
    }
  };

  const onPasteReply = (e) => {
    for (const it of e.clipboardData?.items || []) {
      if (it.type.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) uploadReplyFile(f);
      }
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) {
      // 공개 대기 초안이 있는데 빈 칸으로 누른 경우 — 이 칸은 '새 답변 쓰기'다. 갈 곳을 알려 준다.
      const pending = (thread.posts || []).some((p) => p.draft);
      return alert(
        pending
          ? "이 칸은 새 답변을 쓰는 곳입니다. 위 AI 답변 초안을 내보내시려면 그 아래 '공개하기'를 눌러 주세요."
          : "답변 내용을 입력해주세요.",
      );
    }
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/inquiry/${gid}/reply`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ content: reply, answerPublic, publicTitle: publicTitle || null, attachments: replyAtt }),
      });
      const d = await r.json();
      if (d.success) { setReply(""); setReplyAtt([]); onChanged(); }
      else alert(d.error || "답변 실패");
    } finally { setBusy(false); }
  };

  // 초안으로 들어온 AI 답변을 공개한다 — 공개해야 문의자에게 보이고 상태가 답변완료가 된다.
  const publishAnswer = async (id) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/inquiry/${gid}/publish/${id}`, {
        method: "POST",
        headers: jsonHeaders(),
      });
      const d = await r.json();
      if (d.success) onChanged();
      else alert(d.error || "공개 실패");
    } finally { setBusy(false); }
  };

  const saveVisibility = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/inquiry/${gid}/visibility`, {
        method: "PATCH",
        headers: jsonHeaders(),
        body: JSON.stringify({ answerPublic, publicTitle: publicTitle || null }),
      });
      const d = await r.json();
      if (d.success) onChanged(); else alert(d.error || "저장 실패");
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("이 문의를 삭제할까요?")) return;
    const r = await fetch(`${API_BASE_URL}/api/admin/inquiry/${gid}`, { method: "DELETE", headers: authHeaders() });
    const d = await r.json();
    if (d.success) onDeleted(); else alert(d.error || "삭제 실패");
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, flex: 1 }}>스레드 #{gid}</div>
        <span style={{ ...tag, background: "#eef2ff", color: "#3730a3", marginRight: 6 }}>
          {thread.questionPublic ? "질문공개" : "질문비공개"}
        </span>
        <button onClick={del} style={{ ...btnGhost, color: "#dc2626" }}>삭제</button>
      </div>

      {thread.posts?.map((p) => (
        <div key={p.id} style={{ ...box, marginLeft: p.kind === "answer" ? 20 : 0, borderLeft: p.kind === "answer" ? "3px solid #1b1a20" : p.kind === "followup" ? "3px solid #d97706" : "1px solid #eee", background: p.kind === "answer" ? "#fafbfd" : p.kind === "followup" ? "#fffbeb" : "#fff" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ ...tag, background: p.kind === "answer" ? "#1b1a20" : p.kind === "followup" ? "#d97706" : "#f1f3f8", color: p.kind === "question" ? "#6b7280" : "#fff" }}>
              {p.kind === "answer" ? "답변" : p.kind === "followup" ? "추가질문" : "질문"}
            </span>
            <span style={{ fontSize: 14, fontWeight: 700 }}>{p.kind === "question" ? p.title : p.kind === "answer" ? (p.aiAuthored ? "AI 답변" : "관리자 답변") : "추가 질문"}</span>
            {p.draft && (
              <span data-testid="admin-answer-draft" style={{ ...tag, background: "#e5e7eb", color: "#1b1a20" }}
                    title="아직 공개하지 않은 초안입니다. 문의자에게는 보이지 않습니다.">
                초안 · 공개 전
              </span>
            )}
            <span style={{ fontSize: 12, color: "#6b7280", marginLeft: "auto" }}>{p.author || "-"} · {fmt(p.createdAt)}</span>
          </div>
          <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{p.content}</div>
          {p.attachments?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
              {p.attachments.map((a, i) =>
                /image/.test(a.mime || "") ? (
                  <a key={i} href={a.url} target="_blank" rel="noreferrer">
                    <img src={a.url} alt="" style={{ maxWidth: 200, maxHeight: 200, borderRadius: 6, border: "1px solid #eee" }} />
                  </a>
                ) : (
                  <a key={i} href={a.url} target="_blank" rel="noreferrer" style={btnGhost}>{a.name || "첨부"}</a>
                ),
              )}
            </div>
          )}
          {/* 초안은 본문을 읽은 자리에서 공개한다. 머리줄 구석의 작은 칩은 라벨처럼 보여 버튼 구실을
              못 했다 — 사용자가 아래 '답변 등록'(새 답변 쓰는 칸)을 눌러 "내용을 입력해주세요"를 만났다. */}
          {p.draft && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginTop: 12,
                paddingTop: 10,
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <div style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.6 }}>
                이 답변은 아직 공개되지 않았습니다.
                <br />
                문의자에게는 보이지 않고, 공개해야 답변완료가 됩니다.
              </div>
              <button
                type="button"
                data-testid="admin-answer-publish"
                disabled={busy}
                onClick={() => publishAnswer(p.id)}
                style={{
                  marginLeft: "auto",
                  padding: "8px 18px",
                  border: "none",
                  borderRadius: 6,
                  background: "#1b1a20",
                  color: "#fff",
                  cursor: busy ? "default" : "pointer",
                  fontSize: 13.5,
                  fontWeight: 700,
                }}
              >
                공개하기
              </button>
            </div>
          )}
        </div>
      ))}

      {/* 답변 작성 + 공개설정 */}
      <div style={{ ...box, marginTop: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>답변 작성</div>
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          onPaste={onPasteReply}
          rows={4}
          placeholder="답변(댓글)을 입력하세요. 화면을 캡처해 Ctrl+V 로 바로 붙여넣을 수 있습니다."
          style={input}
        />
        {replyAtt.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "4px 0 8px" }}>
            {replyAtt.map((a, i) => (
              <div key={i} style={attChip}>
                {/image/.test(a.mime || "") ? (
                  <img src={a.url} alt="" style={{ width: 54, height: 54, objectFit: "cover", borderRadius: 6, display: "block" }} />
                ) : (
                  <span style={{ fontSize: 12, padding: "0 6px" }}>{a.name || "첨부"}</span>
                )}
                <button type="button" onClick={() => setReplyAtt((p) => p.filter((_, j) => j !== i))} style={attDel} title="첨부 삭제">✕</button>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginBottom: 4 }}>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple style={{ display: "none" }}
            onChange={(e) => { [...e.target.files].forEach(uploadReplyFile); e.target.value = ""; }} />
          <button type="button" onClick={() => fileRef.current?.click()} style={btnGhost}>파일 첨부</button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, margin: "8px 0", flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={answerPublic} onChange={(e) => setAnswerPublic(e.target.checked)} />
            답변 공개 (모두 열람 — 질문이 비공개여도 답변만 노출)
          </label>
        </div>
        {!thread.questionPublic && answerPublic && (
          <input value={publicTitle} onChange={(e) => setPublicTitle(e.target.value)} placeholder="목록 노출 제목(선택 — 비우면 &quot;[답변 공개 문의]&quot;)" style={input} />
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button onClick={saveVisibility} disabled={busy} style={btnGhost}>공개설정만 저장</button>
          <button onClick={sendReply} disabled={busy} style={{ ...btnPrimary, marginLeft: "auto" }}>{busy ? "처리 중…" : "답변 등록"}</button>
        </div>
      </div>
    </div>
  );
}

const tag = { fontSize: 10.5, fontWeight: 700, padding: "1px 7px", borderRadius: 999 };
const box = { background: "#fff", border: "1px solid #eee", borderRadius: 10, padding: 14, marginBottom: 10 };
const input = { width: "100%", padding: "10px 12px", border: "1px solid #dadce0", borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", resize: "vertical", marginBottom: 4 };
const btnPrimary = { padding: "9px 18px", border: "none", borderRadius: 8, background: "#1b1a20", color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer" };
const btnGhost = { padding: "7px 13px", border: "1px solid #dadce0", borderRadius: 8, background: "#fff", fontSize: 13, color: "#374151", cursor: "pointer", textDecoration: "none" };
const attChip = { position: "relative", border: "1px solid #e5e7eb", borderRadius: 8, padding: 2, background: "#fafafa", display: "flex", alignItems: "center" };
const attDel = { position: "absolute", top: -7, right: -7, width: 18, height: 18, borderRadius: 999, border: "1px solid #ddd", background: "#fff", cursor: "pointer", fontSize: 10, lineHeight: "15px", padding: 0, color: "#666" };
